"use client";
import SectionWrapper from "@/commonComponents/section-wrapper";
import { H6_2 } from "@/styles/theme/components/typography.fontvariant";
import { Divider, Grid, Stack, Typography } from "@mui/material";
import { StaticImageData } from "next/image";
import { motion } from "framer-motion";
import {
  bottomToToptextStagger,
  bottomToToptextStaggerChildren,
} from "@/commonComponents/animations";
import Link from "@cc/link";
import AnimatedButton, { AnimatedButtonProps } from "@cc/animated-button";

export interface PressReleaseProps {
  title?: string;
  gridData: GridCardProps["data"][];
  ButtonProps?: AnimatedButtonProps;
  navId?: string;
}

const PressRelease = (props: PressReleaseProps) => {
  const { title, gridData, ButtonProps, navId } = props;
  return (
    <SectionWrapper
      SectionProps={{
        sx: { background: (theme) => theme.palette.background.paper },
      }}
      SectionHeaderProps={{
        title,
      }}
      navId={navId || "press-release"}
      color={"primary.main"}
    >
      <Grid
        container
        columnSpacing={2}
        rowSpacing={{ xs: 4, md: 7 }}
        component={motion.div}
        variants={bottomToToptextStagger(0.4)}
        initial={"initial"}
        whileInView={"animate"}
        viewport={{ once: true }}
      >
        {gridData.map((item, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            key={index}
            component={motion.div}
            variants={bottomToToptextStaggerChildren}
          >
            <GridCard data={item} />
          </Grid>
        ))}
      </Grid>
      {ButtonProps && ButtonProps.href && ButtonProps.children && (
        <Stack pt={{ xs: 4, md: 7 }} alignItems={"center"}>
          <AnimatedButton {...ButtonProps} />
        </Stack>
      )}
    </SectionWrapper>
  );
};

export default PressRelease;

type GridCardProps = {
  data: {
    title: string;
    image: StaticImageData | string;
    prefix: {
      company: string;
      date: string;
    };
    href: string;
  };
};

const GridCard = (props: GridCardProps) => {
  const { prefix, title, image, href } = props.data;
  return (
    <Link href={href} style={{ textDecoration: "none" }}>
      <Stack
        width={1}
        height={363}
        sx={{
          background: (theme) =>
            `${theme.palette.gradient.transparentToDark_V2}, url(${
              typeof image === "string" ? image : image.src
            })`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
        justifyContent={"flex-end"}
      >
        <Stack
          m={{ xs: 2, md: 4 }}
          rowGap={{ xs: 1.5, md: 2 }}
          color={"common.white"}
        >
          {prefix && (
            <Stack
              direction={"row"}
              gap={1.5}
              divider={
                <Divider
                  orientation="vertical"
                  sx={{ width: "1px", opacity: 0.5 }}
                />
              }
            >
              {prefix.company && (
                <Typography variant="body2">{prefix.company}</Typography>
              )}
              {prefix.date && (
                <Typography variant="body2">{prefix.date}</Typography>
              )}
            </Stack>
          )}
          {title && (
            <Typography variant="body1" fontSize={H6_2}>
              {title}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Link>
  );
};
